import { COMMENT_SUFFIX, commentKeyOf } from "./authoring.js";
import type { InstrumentNameUnbranded } from "./name.js";
import {
  closedValuesOf,
  commentTargetOf,
  type PublishedInstrumentSchema,
} from "./publishedSchema.js";

/**
 * How every identifier an instrument declares is spelt: lowercase snake_case,
 * starting with a letter, with no doubled underscore.
 *
 * These strings outlive every collector and are read as identifiers wherever
 * they go — a property in code, a step in a Postgres jsonpath, a column in an
 * analytics model, a field in a third-party sender's class — so they are spelt
 * to be one, unquoted, in all of them. `__` is reserved for joining a group to
 * an item in a flattened column, which a key containing one would make
 * ambiguous.
 */
const IDENTIFIER = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;

/**
 * Postgres's identifier limit, in bytes: a key longer than this is truncated
 * where it is a column name, so two keys sharing a prefix would become one
 * column. A joined `group__item` may still exceed it; that is the model's to
 * shorten.
 */
export const IDENTIFIER_MAX_LENGTH = 63;

/** Whether text is spelt as a group or item key may be, and fits the limit. */
export const isIdentifier = (text: string): boolean =>
  IDENTIFIER.test(text) && text.length <= IDENTIFIER_MAX_LENGTH;

/**
 * A name segment after the family as it may be published: snake_case words, or
 * a version's dot-separated parts. Hyphens are what the rule exists to keep out.
 */
const NAME_SEGMENT = /^[a-z0-9]+(?:[._][a-z0-9]+)*$/;

/**
 * Text respelt as lowercase snake_case words. An acronym ends where a
 * capitalised word begins (`getNHSNumber` is `get_nhs_number`), since these
 * suggestions become permanent keys.
 */
const snakeWordsOf = (text: string): string =>
  text
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1_$2")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

/** The snake_case spelling to suggest, or nothing where no mechanical one would pass. */
const snakeSpellingOf = (identifier: string): string | undefined => {
  const suggested = snakeWordsOf(identifier);

  return isIdentifier(suggested) ? suggested : undefined;
};

/** Spelling and length are judged apart, so one round trip reports both. */
const identifierIssue = (at: string, identifier: string): string[] => {
  const issues: string[] = [];

  if (!IDENTIFIER.test(identifier)) {
    const suggestion = snakeSpellingOf(identifier);

    issues.push(
      suggestion === undefined
        ? `${at}: "${identifier}" is not lowercase snake_case`
        : `${at}: "${identifier}" is not lowercase snake_case; write "${suggestion}"`,
    );
  }

  if (identifier.length > IDENTIFIER_MAX_LENGTH) {
    issues.push(
      `${at}: "${identifier}" is longer than ${String(IDENTIFIER_MAX_LENGTH)} characters`,
    );
  }

  return issues;
};

/** The string values of a closed set; numbers are scores, not identifiers. */
const stringValuesOf = (values: readonly unknown[] | undefined): string[] =>
  (values ?? []).filter((value) => typeof value === "string");

/**
 * Every identifier a published schema declares that is not spelt as one:
 * its groups, its items, the string values of its closed sets, and each part
 * of a response set's dotted id.
 *
 * Each is reported where it is declared, so a value in a shared set is reported
 * once, at the set, rather than at every item drawing on it.
 */
export const identifierSpellingIssues = (
  schema: PublishedInstrumentSchema,
): string[] => {
  const defs = schema.$defs ?? {};

  const declared = Object.entries(schema.properties).flatMap(
    ([group, groupSchema]) => [
      ...identifierIssue(`/properties/${group}`, group),
      ...Object.entries(groupSchema.properties).flatMap(
        ([item, itemSchema]) => {
          const at = `/properties/${group}/properties/${item}`;
          const response = itemSchema.properties.response;

          return [
            ...identifierIssue(at, item),
            ...(typeof response["$ref"] === "string"
              ? []
              : stringValuesOf(closedValuesOf(response, defs)).flatMap(
                  (value) => identifierIssue(`${at}/response`, value),
                )),
          ];
        },
      ),
    ],
  );

  const shared = Object.entries(defs).flatMap(([id, definition]) => [
    ...id.split(".").flatMap((part) => identifierIssue(`/$defs/${id}`, part)),
    ...stringValuesOf(closedValuesOf(definition, defs)).flatMap((value) =>
      identifierIssue(`/$defs/${id}`, value),
    ),
  ]);

  return [...declared, ...shared];
};

/**
 * Every key that breaks the pairing of a comment with its item by name: a
 * comment is keyed by its item's key and `COMMENT_SUFFIX`, and nothing else may
 * end in it, so a reader without the schema pairs them correctly.
 */
export const commentKeyIssues = (schema: PublishedInstrumentSchema): string[] =>
  Object.entries(schema.properties).flatMap(([group, groupSchema]) =>
    Object.entries(groupSchema.properties).flatMap(([item, itemSchema]) => {
      const at = `/properties/${group}/properties/${item}`;
      const target = commentTargetOf(itemSchema);

      if (target === undefined) {
        return item.endsWith(COMMENT_SUFFIX)
          ? [
              `${at}: "${item}" ends in "${COMMENT_SUFFIX}", which only a declared comment may`,
            ]
          : [];
      }

      const expected = commentKeyOf(target);

      return item === expected
        ? []
        : [`${at}: is a comment on "${target}", so it is keyed "${expected}"`];
    }),
  );

/**
 * Every segment of a name that may not be published as it is spelt.
 *
 * The family, the first segment, is the namespace keys live in and is held to
 * the identifier rule. What the later segments mean is the instrument owner's,
 * so they need only be snake_case words or a version's dotted parts.
 */
export const nameSpellingIssues = (name: InstrumentNameUnbranded): string[] => [
  ...identifierIssue("name family", name[0]),
  ...name
    .slice(1)
    .filter((segment) => !NAME_SEGMENT.test(segment))
    .map((segment) => {
      // Respelt part by part, so a version's dots survive the suggestion.
      const suggestion = segment.split(".").map(snakeWordsOf).join(".");

      return NAME_SEGMENT.test(suggestion)
        ? `name segment "${segment}" is not lowercase snake_case; write "${suggestion}"`
        : `name segment "${segment}" is not lowercase snake_case`;
    }),
];
